/*
Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
  distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
  prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
  content, please contact the author for proper authorization and agreement.
  */

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BellIcon, MoonIcon, PlusCircleIcon, SunIcon, } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { useTypedSelector } from '../helpers/reducers'
import { useLocation } from 'react-router-dom'
import { resolveTitleFromPath } from 'utils/utility'
import { useUiTheme } from 'theme/UiThemeProvider'
import UiLanguageSwitcher from 'components/Ui/UiLanguageSwitcher'
import UiNotificationsDropdown from 'components/Ui/UiNotificationsDropdown'
import UiUserMenu from 'components/Ui/UiUserMenu'

import SessionAction from 'store/session/SessionAction'
import { closeModal, openModal } from 'helpers/actions'
import { ModalSize } from 'types/enums/ModalSize'
import CreateReminderModal from 'components/Reminders/CreateReminderModal'
import { useAppDispatch } from 'helpers/store'
import { useCurrentUser } from 'utils/hooks/useCurrentUser'

interface HeadbarProps {}

const Headbar: React.FC<HeadbarProps> = () => {
    const { t } = useTranslation()
    const location = useLocation()
    const { mode, toggleMode } = useUiTheme()

    const dispatch = useAppDispatch()
    const { user, fullName } = useCurrentUser()
    const headbarTitle = useTypedSelector((state) => state.ui.headbarTitle)
    const remindersContext = useTypedSelector(
        (state) => state.ui.remindersContext
    )
    const notifications =
        useTypedSelector((state) => state.sessionState.notifications) || []

    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false)

    const currentTitle =
        headbarTitle || t(resolveTitleFromPath(location.pathname))

    const unreadCount = notifications.filter((n) => !n.read_at).length
    const userName = !user ? t('User') : fullName

    const toggleNotificationsHandler = () => {
        setIsNotificationsOpen(!isNotificationsOpen)
    }

    useEffect(() => {
        document.title = String(currentTitle)
    }, [currentTitle])

    useEffect(() => {
        dispatch(SessionAction.fetchNotifications() as any)
    }, [dispatch])

    const handleAddReminder = () => {
        const context =
            remindersContext?.modelId && remindersContext?.modelName
                ? {
                      id: remindersContext.modelId,
                      name: remindersContext.modelName,
                  }
                : undefined

        dispatch(
            openModal(
                <CreateReminderModal
                    modelContext={context}
                    onSuccess={() => {}}
                />,
                {
                    modalSize: ModalSize.Small,
                    closeModal: () => dispatch(closeModal()),
                },
                t('Create Reminder')
            )
        )
    }

    return (
        <header className="Headbar">
            {/* Left Section: Welcome Message (Aligned over Sidebar center) */}
            <div className={clsx('hidden xl:flex items-center justify-start pl-2 xl:pl-4 min-w-45')}>
                <span
                    className={clsx(
                        'text-[13px] font-semibold tracking-wide text-gray-700 dark:text-gray-200 whitespace-nowrap'
                    )}
                >
                    {t('Welcome back')}, {userName}
                </span>
            </div>

            {/* Center Section: Page Title */}
            <div
                className={clsx(
                    'flex-1 min-w-0 text-gray-900 dark:text-gray-100 flex items-center justify-center px-2'
                )}
            >
                <h1 className={clsx('text-base xl:text-lg font-bold max-w-full whitespace-nowrap truncate')}>
                    {currentTitle}
                </h1>
            </div>

            {/* Right Section: Actions */}
            <div
                className={clsx(
                    'flex items-center gap-2 xl:gap-3 pr-2 xl:pr-4 text-gray-800 dark:text-gray-100 max-w-[58vw]'
                )}
            >
                <div className={clsx('relative flex items-center')}>
                    {/* Add Reminder Button */}
                    {remindersContext?.isVisible &&
                        remindersContext?.modelId &&
                        remindersContext?.modelName && (
                            <button
                                type="button"
                                title={t('Add Reminder')}
                                onClick={handleAddReminder}
                                className={clsx(
                                    'flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors mr-2',
                                    'bg-blue-50 hover:bg-blue-100 text-blue-700',
                                    'dark:bg-blue-900/30 dark:hover:bg-blue-900/50 dark:text-blue-300',
                                    'text-sm font-medium'
                                )}
                            >
                                <PlusCircleIcon className={clsx('size-4')} />
                                <span className={clsx('hidden xl:inline')}>{t('Add Reminder')}</span>
                            </button>
                        )}

                    {/* Notifications */}
                    <button
                        type="button"
                        title={t('Notifications')}
                        onClick={toggleNotificationsHandler}
                        className={clsx(
                            'relative p-2 rounded-full transition-colors hover:bg-gray-100 dark:hover:bg-gray-800'
                        )}
                    >
                        <BellIcon className={clsx('size-6')} />
                        {unreadCount > 0 && (
                            <span
                                className={clsx(
                                    'absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full size-4 flex items-center justify-center'
                                )}
                            >
                                {unreadCount > 9 ? '9+' : unreadCount}
                            </span>
                        )}
                    </button>
                    <UiNotificationsDropdown
                        isOpen={isNotificationsOpen}
                        onClose={() => setIsNotificationsOpen(false)}
                    />
                </div>

                {/* Theme Toggle */}
                <button
                    type="button"
                    onClick={toggleMode}
                    className={clsx(
                        'flex items-center justify-center p-2 rounded-full transition-colors hover:bg-gray-100 dark:hover:bg-gray-800'
                    )}
                    title={
                        mode === 'dark'
                            ? t('Switch to light mode')
                            : t('Switch to dark mode')
                    }
                >
                    {mode === 'dark' ? (
                        <SunIcon className={clsx('size-5')} />
                    ) : (
                        <MoonIcon className={clsx('size-5')} />
                    )}
                </button>

                <UiLanguageSwitcher />

                <UiUserMenu />
            </div>
        </header>
    )
}

export default Headbar
